import React from "react";

interface JsonLdProps {
  data: Record<string, unknown>;
}

export function JsonLd({ data }: JsonLdProps) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export function generateCalculatorSchema(name: string, description: string, url: string, imageUrl?: string) {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${url}#software`,
    "name": name,
    "description": description,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "All",
    "offers": {
      "@type": "Offer",
      "price": 0,
      "priceCurrency": "USD"
    },
    "publisher": {
      "@type": "Organization",
      "name": siteName,
      "url": url.split("/calculators")[0]
    },
    "url": url
  };

  if (imageUrl) {
    schema["image"] = imageUrl;
  }

  return schema;
}

export function generateWebsiteSchema(url: string) {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const baseUrl = url.endsWith("/") ? url : `${url}/`;
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${baseUrl}#website`,
    "name": siteName,
    "url": baseUrl,
    "publisher": {
      "@type": "Organization",
      "@id": `${baseUrl}#organization`,
      "name": siteName
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}calculators/?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
}

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  const lastItemUrl = items.length > 0 ? items[items.length - 1].url : "";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": lastItemUrl ? `${lastItemUrl}#breadcrumb` : undefined,
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
}

export function generateArticleSchema(
  article: {
    title: string;
    description: string;
    slug: string;
    createdAt: string;
    updatedAt: string;
    category: string;
    tags?: string[];
    author?: { name: string; url?: string };
    sources?: { name: string; url: string }[];
  },
  siteUrl: string,
  imageUrl?: string
) {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const authorName = article.author?.name || `${siteName} Editorial Team`;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${siteUrl}/articles/${article.slug}/#article`,
    "headline": article.title,
    "description": article.description,
    "datePublished": new Date(article.createdAt).toISOString(),
    "dateModified": new Date(article.updatedAt).toISOString(),
    "author": {
      "@type": "Person",
      "@id": article.author?.url ? `${siteUrl}${article.author.url}#person` : `${siteUrl}/about/#organization`,
      "name": authorName,
      "url": article.author?.url ? `${siteUrl}${article.author.url}` : `${siteUrl}/about/`
    },
    "publisher": {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      "name": siteName,
      "url": siteUrl
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/articles/${article.slug}/`
    }
  };

  if (imageUrl) {
    schema["image"] = imageUrl;
    schema["mainEntityOfPage"] = {
      "@type": "WebPage",
      "@id": `${siteUrl}/articles/${article.slug}/`,
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": imageUrl
      }
    };
  }

  if (article.tags && article.tags.length > 0) {
    schema["keywords"] = article.tags.join(", ");
  }

  if (article.sources && article.sources.length > 0) {
    schema["citation"] = article.sources.map((s) => ({
      "@type": "CreativeWork",
      "url": s.url
    }));
  }

  return schema;
}

export function generateFaqSchema(faqs: { question: string; answer: string }[], url?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": url ? `${url}#faq` : "#faq",
    "mainEntityOfPage": url ? { "@id": `${url}#software` } : undefined,
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

export function generateContactPageSchema(siteUrl: string) {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${siteUrl}/contact/#webpage`,
    "url": `${siteUrl}/contact/`,
    "name": `Contact Us | ${siteName}`,
    "description": `Get in touch with the ${siteName} team.`,
    "mainEntity": {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      "name": siteName,
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": `support@${siteUrl.replace("https://www.", "").replace("https://", "")}`
      }
    }
  };
}

export function generateHowToSchema(
  name: string,
  description: string,
  url: string,
  steps: { name: string; text: string }[]
) {
  const cleanToolName = name
    .replace("How to Calculate ", "")
    .replace("How to Estimate ", "")
    .replace("How to Size and Estimate ", "")
    .replace("How to Plan a ", "")
    .trim();

  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "@id": `${url}#howto`,
    "name": name,
    "description": description,
    "url": url,
    "tool": [
      {
        "@type": "HowToTool",
        "name": `${cleanToolName} Calculator`
      }
    ],
    "step": steps.map((step, idx) => ({
      "@type": "HowToStep",
      "@id": `${url}#step-${idx + 1}`,
      "position": idx + 1,
      "name": step.name,
      "text": step.text,
      "url": `${url}#step-${idx + 1}`
    }))
  };
}

